const STORAGE_KEY = 'charjer.token';

type Listener = (token: string | null) => void;

/**
 * The access token, owned by the API layer.
 *
 * It lives here rather than in the auth feature so the axios interceptor can
 * read it without importing upwards from `shared/` into `features/` — the
 * dependency direction stays one-way. The auth feature is the only writer.
 *
 * The backend issues a long-lived JWT and exposes **no refresh endpoint**, so
 * there is no silent-refresh flow to implement: a 401 ends the session. Should
 * `/auth/refresh` ever ship, it plugs into `client.ts`'s response interceptor
 * and nothing else changes.
 *
 * Persisted in `localStorage` because the token must survive the full-page
 * redirect to and back from the payment gateway; `sessionStorage` is lost when
 * the gateway opens the return URL in a fresh context on some mobile browsers.
 */
function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private-mode Safari and locked-down WebViews throw on access.
    return null;
  }
}

let currentToken: string | null = readStoredToken();
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener(currentToken);
}

/**
 * Members are arrow properties, not methods: `get` and `subscribe` are passed
 * straight to `useSyncExternalStore`, so they must stay callable detached from
 * the object.
 */
export const sessionToken = {
  get: (): string | null => currentToken,

  set: (token: string): void => {
    currentToken = token;
    try {
      window.localStorage.setItem(STORAGE_KEY, token);
    } catch {
      // Non-persistent session is still a usable session.
    }
    notify();
  },

  clear: (): void => {
    currentToken = null;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clean up */
    }
    notify();
  },

  /** Subscribe to changes; returns the unsubscribe function. */
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
} as const;

/**
 * Keep tabs in sync: logging out in one tab must not leave another tab holding
 * a stale session. Fired by the browser only for *other* documents.
 */
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  const next = readStoredToken();
  if (next === currentToken) return;
  currentToken = next;
  notify();
});
